"use client"

import { FightPlanEditor } from "@/components/fight-plan-editor"
import { ActiveTimeModeSelect } from "@/components/active-time-mode-select"
import { ExpiryDateCascader } from "@/components/expiry-date-cascader"
import { useState, useEffect, useRef } from "react"
import { validateFightPlan } from "@/lib/fight-plan"
import { useToast } from "@/hooks/use-toast"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { CaretDown as ChevronDown, User, FloppyDisk as Save, X, Gear as Settings, Bell, CalendarBlank as Calendar, Sword, Target } from "@phosphor-icons/react"
import { Tag, Divider, Icon, Button, Input } from "animal-island-ui"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ConfigSwitch } from "@/components/config-switch"
import { EditNumberStepper } from "@/components/edit-number-stepper"
import item001 from "animal-island-ui/items/item-001.png"
import item002 from "animal-island-ui/items/item-002.png"
import item003 from "animal-island-ui/items/item-003.png"
import item004 from "animal-island-ui/items/item-004.png"
import item005 from "animal-island-ui/items/item-005.png"
import item006 from "animal-island-ui/items/item-006.png"

export interface UserAccount {
  id: number
  name: string
  account: string
  password?: string
  freeze: number
  server: number
  taskType: string
  refresh: number
  agent: string | null
  createTime: string
  updateTime: string
  expireTime: string
  san: string
  config: any
  active: any
  notice: any
  days?: number
}

interface UserEditDialogProps {
  user: UserAccount | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (user: Partial<UserAccount>) => Promise<boolean>
}

function isHashedPassword(value?: string) {
  return !!value?.startsWith("sha256$")
}

export function UserEditDialog({ user, open, onOpenChange, onSave }: UserEditDialogProps) {
  const { toast } = useToast()
  const savingRef = useRef(false)
  const draftUserId = useRef<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [editForm, setEditForm] = useState<Partial<UserAccount>>({})
  const [addMonths, setAddMonths] = useState<number>(0)
  const [fightHelpOpen, setFightHelpOpen] = useState(false)
  const [passwordWasHashed, setPasswordWasHashed] = useState(false)

  useEffect(() => {
    if (!open || !user) {
      draftUserId.current = null
      return
    }
    if (draftUserId.current !== user.id) {
      draftUserId.current = user.id
      setAddMonths(0)
      const hashed = isHashedPassword(user.password)
      setPasswordWasHashed(hashed)
      setEditForm(structuredClone({
        name: user.name,
        account: user.account,
        password: hashed ? "" : (user.password || ""),
        freeze: user.freeze,
        server: user.server,
        taskType: user.taskType,
        refresh: user.refresh,
        agent: user.agent,
        expireTime: user.expireTime,
        config: user.config || {},
        active: user.active || {},
        notice: user.notice || {},
      }))
    }
  }, [user, open])

  if (!user) return null

  const handleSave = async () => {
    if (savingRef.current) return
    const fightError = validateFightPlan(editForm.config?.daily?.fight ?? [])
    if (fightError) {
      toast({ variant: "destructive", title: "作战配置有误", description: fightError })
      return
    }
    savingRef.current = true
    setLoading(true)
    try {
      const payload = structuredClone(addMonths > 0 ? { ...editForm, expireTime: addMonthsToExpire(addMonths) } : editForm)
      if (await onSave(payload) === true) onOpenChange(false)
    } catch (error) {
      toast({ variant: "destructive", title: "保存失败", description: error instanceof Error ? error.message : "请稍后重试，草稿已保留。" })
    } finally {
      savingRef.current = false
      setLoading(false)
    }
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!savingRef.current) onOpenChange(nextOpen)
  }

  const updateNotice = (type: string, field: string, value: any) => {
    setEditForm({
      ...editForm,
      notice: {
        ...editForm.notice,
        [type]: {
          ...editForm.notice?.[type],
          [field]: value,
        },
      },
    })
  }

  const updateActive = (day: string, enabled: boolean) => {
    setEditForm({
      ...editForm,
      active: {
        ...editForm.active,
        [day]: {
          ...(editForm.active?.[day] || {}),
          enable: enabled,
        },
      },
    })
  }

  const updateActiveDetail = (day: string, details: string[]) => {
    setEditForm({
      ...editForm,
      active: {
        ...editForm.active,
        [day]: {
          ...(editForm.active?.[day] || {}),
          enable: true,
          detail: details,
        },
      },
    })
  }

  const updateConfig = (path: string[], value: any) => {
    setEditForm(previous => {
      const newConfig = structuredClone(previous.config || {})
      let current = newConfig
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]]) current[path[i]] = {}
        current = current[path[i]]
      }
      current[path[path.length - 1]] = structuredClone(value)
      return { ...previous, config: newConfig }
    })
  }

  const getConfigValue = (path: string[], defaultValue: any = null) => {
    let current = editForm.config
    for (const key of path) {
      if (current && typeof current === "object" && key in current) {
        current = current[key]
      } else {
        return defaultValue
      }
    }
    return current
  }

  const formatDateForInput = (dateString: string) => {
    if (!dateString) return ""
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return ""
    const pad = (value: number) => String(value).padStart(2, "0")
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
  }

  // 加月: base = (未过期 ? 原到期时间 : 今天)
  // 提示文案: "在「到期时间」基础上累加月数（基于当前到期；过期或空则从今天起算）"
  const addMonthsToExpire = (months: number) => {
    const now = new Date()
    const expire = editForm.expireTime ? new Date(editForm.expireTime) : null
    const base = expire && expire > now ? expire : now
    const day = base.getDate()
    base.setDate(1)
    base.setMonth(base.getMonth() + months)
    const lastDay = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate()
    base.setDate(Math.min(day, lastDay))
    const newIso = formatDateForInput(base.toISOString()).split("T")[0] + "T23:59:59"
    return newIso
  }

  const stageOptions = [
    { value: 'jm', label: '剿灭' },
    { value: 'ce', label: '龙门币' },
    { value: 'ls', label: '经验卡' },
    { value: 'ap', label: '红票' },
    { value: 'ca', label: '技能书' },
    { value: '1-7', label: '1-7' },
    { value: 'hd', label: '活动材料关' },
    { value: 'pr2', label: '大芯片任意' },
    { value: 'pr1', label: '小芯片任意' },
    { value: 'hd-10', label: '活动第十关' },
    { value: 'hd-9', label: '活动第九关' },
    { value: 'hd-8', label: '活动第八关' },
    { value: 'hd-7', label: '活动第七关' },
    { value: 'hd-6', label: '活动第六关' },
    { value: 'hd-5', label: '活动第五关' },
    { value: '近卫2', label: '近卫芯片[大]' },
    { value: '近卫1', label: '近卫芯片[小]' },
    { value: '特种2', label: '特种芯片[大]' },
    { value: '特种1', label: '特种芯片[小]' },
    { value: '医疗2', label: '医疗芯片[大]' },
    { value: '医疗1', label: '医疗芯片[小]' },
    { value: '重装2', label: '重装芯片[大]' },
    { value: '重装1', label: '重装芯片[小]' },
    { value: '辅助2', label: '辅助芯片[大]' },
    { value: '辅助1', label: '辅助芯片[小]' },
    { value: '狙击2', label: '狙击芯片[大]' },
    { value: '狙击1', label: '狙击芯片[小]' },
    { value: '术士2', label: '术士芯片[大]' },
    { value: '术士1', label: '术士芯片[小]' },
    { value: '先锋2', label: '先锋芯片[大]' },
    { value: '先锋1', label: '先锋芯片[小]' }
  ]


  const dayNames: Record<string, string> = {
    monday: "周一",
    tuesday: "周二",
    wednesday: "周三",
    thursday: "周四",
    friday: "周五",
    saturday: "周六",
    sunday: "周日",
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="admin-user-edit max-w-6xl bg-cream-50">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-leaf-900">
            <User className="h-5 w-5" />
            编辑用户 - {user.name}
          </DialogTitle>
          <DialogDescription className="text-leaf-700">修改用户的详细信息和配置</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue={typeof window !== "undefined" ? (new URLSearchParams(window.location.search).get("dev_tab") || "basic") : "basic"} className="admin-user-edit__tabs w-full">
          <TabsList className="grid w-full grid-cols-2 gap-1 bg-cream-100 border-2 border-leaf-500 p-2 rounded-2xl h-auto">
            <TabsTrigger value="basic" className="data-[state=active]:bg-leaf-500 data-[state=active]:text-white data-[state=active]:shadow-none text-leaf-900 font-semibold rounded-xl px-2 py-1.5">基本信息</TabsTrigger>
            <TabsTrigger value="config" className="data-[state=active]:bg-leaf-500 data-[state=active]:text-white data-[state=active]:shadow-none text-leaf-900 font-semibold rounded-xl px-2 py-1.5">任务配置</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4">
            <div className="admin-user-edit__basic grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="name">
                  用户名
                </label>
                <Input
                  id="name"
                  value={editForm.name || ""}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="bg-leaf-100 border-leaf-500 text-leaf-900"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="account">
                  账号
                </label>
                <Input
                  id="account"
                  value={editForm.account || ""}
                  onChange={(e) => setEditForm({ ...editForm, account: e.target.value })}
                  className="bg-leaf-100 border-leaf-500 text-leaf-900"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="password">
                  密码
                </label>
                <Input
                  id="password"
                  type="text"
                  placeholder={passwordWasHashed ? "请重新输入游戏密码" : "留空则不修改密码"}
                  value={editForm.password || ""}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  className="bg-leaf-100 border-leaf-500 text-leaf-900"
                />
                {passwordWasHashed && (
                  <p className="text-sm text-amber-700">
                    该账号密码曾被加密存储，无法显示原文。留空不修改；若要恢复正常请重新输入明文密码。
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label>任务类型</label>
                <Select
                  value={editForm.taskType || ""}
                  onValueChange={(value) => setEditForm({ ...editForm, taskType: value })}
                >
                  <SelectTrigger className="bg-leaf-100 border-leaf-500 text-leaf-900">
                    <SelectValue placeholder="选择任务类型" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">日常任务</SelectItem>
                    <SelectItem value="luxury">豪华套餐</SelectItem>
                    <SelectItem value="rogue">肉鸽·傀影与猩红孤钻</SelectItem>
                    <SelectItem value="rogue2">肉鸽·水月与深蓝之树</SelectItem>
                    <SelectItem value="sami">肉鸽·探索者的银凇止境</SelectItem>
                    <SelectItem value="sarkaz">肉鸽·萨卡兹的无终奇语</SelectItem>
                    <SelectItem value="sui">肉鸽·界园</SelectItem>
                    <SelectItem value="blackflow">肉鸽·黑流树海·寻森小队</SelectItem>
                    <SelectItem value="sand_fire">生息演算·沙洲遗闻</SelectItem>
                    <SelectItem value="icebreaker">生息演算·重启锚点</SelectItem>
                    <SelectItem value="goods">商品任务</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label>服务器</label>
                <Select
                  value={editForm.server?.toString() || ""}
                  onValueChange={(value) => setEditForm({ ...editForm, server: Number.parseInt(value) })}
                >
                  <SelectTrigger className="bg-leaf-100 border-leaf-500 text-leaf-900">
                    <SelectValue placeholder="选择服务器" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">官服</SelectItem>
                    <SelectItem value="1">B服</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label htmlFor="refresh">
                  刷新次数
                </label>
                <EditNumberStepper id="refresh" value={editForm.refresh || 0} onValueChange={value=>setEditForm({...editForm,refresh:value})}/>
              </div>

              <div className="space-y-2">
                <label htmlFor="addMonths">
                  快捷续期（按月）
                </label>
                <div className="edit-renewal-row">
                  <EditNumberStepper id="addMonths" min={0} max={120} value={addMonths} onValueChange={setAddMonths}/>
                  {[1, 3, 6, 12].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setAddMonths(n)}
                      aria-pressed={addMonths === n}
                      className="px-2 py-1 border rounded border-leaf-500 text-leaf-900 hover:bg-leaf-50 text-xs"
                    >
                      {n}月
                    </button>
                  ))}
                </div>
                <p className="text-xs text-leaf-700">{addMonths > 0 ? `保存后续期 ${addMonths} 个月，到期：${addMonthsToExpire(addMonths).split("T")[0]}` : "0 表示不续期；选择月数后统一保存，过期或空则从今天起算。"}</p>
              </div>

              <div className="space-y-2">
                <label htmlFor="expireDate">
                  到期日期
                </label>
                <ExpiryDateCascader value={formatDateForInput(editForm.expireTime || "").split("T")[0]} onChange={date => setEditForm({ ...editForm, expireTime: date + "T23:59:59" })}/>
                <p className="text-xs">有效至所选日期当天结束，保存后生效。</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <ConfigSwitch
                id="freeze"
                checked={!!editForm.freeze}
                onChange={(checked) => setEditForm({ ...editForm, freeze: checked ? 1 : 0 })}
              />
              <label htmlFor="freeze">
                冻结账号
              </label>
            </div>
          </TabsContent>

<TabsContent value="config" className="space-y-3"><p className="text-sm">按需展开，修改后保存</p><details className="config-section"><summary>基础配置<ChevronDown size={17} aria-hidden="true"/></summary><div className="config-body">                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {[
                      { key: "mail", label: "邮件领取" },
                      { key: "friend", label: "好友访问" },
                      { key: "credit", label: "信用商店" },
                      { key: "task", label: "任务领取" },
                      { key: "activity", label: "限时活动" },
                    ].map((item) => (
                      <div key={item.key} className="flex items-center space-x-2">
                        <ConfigSwitch
                          id={`daily_${item.key}`}
                          checked={getConfigValue(["daily", item.key], false)}
                          onChange={(checked) => updateConfig(["daily", item.key], !!checked)}
                        />
                        <label htmlFor={`daily_${item.key}`} className="text-sm text-leaf-900">
                          {item.label}
                        </label>
                      </div>
                    ))}
                  </div>

</div></details><details className="config-section"><summary><span className="edit-fight-heading">作战配置<button type="button" className="edit-fight-help" aria-label="作战配置说明" onClick={e=>{e.preventDefault();e.stopPropagation();setFightHelpOpen(true)}}>说明</button></span><ChevronDown size={17} aria-hidden="true"/></summary><div className="config-body"><label className="edit-time-heading"><span>启用作战</span><ConfigSwitch checked={getConfigValue(["daily","fight_enable"],false)} onChange={v=>updateConfig(["daily","fight_enable"],!!v)}/></label>
<FightPlanEditor hideToolbar helpOpen={fightHelpOpen} onHelpOpenChange={setFightHelpOpen} value={getConfigValue(["daily","fight"],[])} options={stageOptions} onChange={v=>updateConfig(["daily","fight"],v)}/>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-leaf-900">吃药次数</label>
                      <EditNumberStepper value={getConfigValue(["daily","sanity","drug"],0)} max={99} onValueChange={value=>updateConfig(["daily","sanity","drug"],value)}/>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-leaf-900">碎石次数</label>
                      <EditNumberStepper value={getConfigValue(["daily","sanity","stone"],0)} max={99} onValueChange={value=>updateConfig(["daily","sanity","stone"],value)}/>
                    </div>
                  </div>

</div></details><details className="config-section"><summary>基建配置<ChevronDown size={17} aria-hidden="true"/></summary><div className="config-body">                  <div>
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-4">
                      {[
                        { key: "harvest", label: "基建收货" },
                        { key: "shift", label: "基建换班" },
                        { key: "acceleration", label: "基建加速" },
                        { key: "communication", label: "线索交流" },
                        { key: "deputy", label: "基建副手" },
                      ].map((item) => (
                        <div key={item.key} className="flex items-center space-x-2">
                          <ConfigSwitch
                            id={`infra_${item.key}`}
                            checked={getConfigValue(["daily", "infrastructure", item.key], false)}
                            onChange={(checked) =>
                              updateConfig(["daily", "infrastructure", item.key], !!checked)
                            }
                          />
                          <label htmlFor={`infra_${item.key}`} className="text-sm text-leaf-900">
                            {item.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
</div></details><details className="config-section"><summary>公招配置<ChevronDown size={17} aria-hidden="true"/></summary><div className="config-body">                  <div>
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="flex items-center space-x-2">
                        <ConfigSwitch
                          id="offer_enable"
                          checked={getConfigValue(["daily", "offer", "enable"], true)}
                          onChange={(checked) => updateConfig(["daily", "offer", "enable"], !!checked)}
                        />
                        <label htmlFor="offer_enable" className="text-sm text-leaf-900">
                          启用招募
                        </label>
                      </div>
                      {[
                        { key: "car", label: "招募小车" },
                        { key: "star4", label: "招募4星" },
                        { key: "star5", label: "招募5星" },
                        { key: "star6", label: "招募6星" },
                        { key: "other", label: "招募其他" },
                      ].map((item) => (
                        <div key={item.key} className="flex items-center space-x-2">
                          <ConfigSwitch
                            id={`offer_${item.key}`}
                            checked={getConfigValue(["daily", "offer", item.key], false)}
                            onChange={(checked) => updateConfig(["daily", "offer", item.key], !!checked)}
                          />
                          <label htmlFor={`offer_${item.key}`} className="text-sm text-leaf-900">
                            {item.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

</div></details><details className="config-section"><summary>上号时间设置<ChevronDown size={17} aria-hidden="true"/></summary><div className="config-body">              <p className="text-xs text-gray-500 text-leaf-700">
                设置每天允许上号的时间。选择自定义时段后可编辑；未添加时间段表示全天允许。
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.keys(dayNames).map((day) => {
                  const detail: string[] = editForm.active?.[day]?.detail || []
                  return (
                    <div key={day} className="edit-time-day p-3 border rounded-lg border-leaf-500 space-y-2">
<div className="edit-time-heading"><strong>{dayNames[day]}</strong><ActiveTimeModeSelect day={dayNames[day]} value={!editForm.active?.[day]?.enable?"none":detail.length?"custom":"all"} onChange={mode=>setEditForm({...editForm,active:{...editForm.active,[day]:{...editForm.active?.[day],enable:mode!=="none",detail:mode==="custom"?(detail.length?detail:["18:00-20:00"]):[]}}})}/></div>
{editForm.active?.[day]?.enable && detail.length > 0 && <>
                      {detail.map((slot, idx) => {
                        const [start, end] = slot.split("-")
                        return (
                          <div key={idx} className="edit-time-slot flex items-center gap-2">
                            <Input
                              type="time"
                              value={start || "08:00"}
                              onChange={(e) => {
                                const next = [...detail]
                                next[idx] = `${e.target.value}-${end || "23:00"}`
                                updateActiveDetail(day, next)
                              }}
                              className="flex-1 h-8"
                            />
                            <span className="text-gray-500 text-leaf-700">至</span>
                            <Input
                              type="time"
                              value={end || "23:00"}
                              onChange={(e) => {
                                const next = [...detail]
                                next[idx] = `${start || "08:00"}-${e.target.value}`
                                updateActiveDetail(day, next)
                              }}
                              className="flex-1 h-8"
                            />
                            <Button
                             
                              size="small"
                              danger
                             
                              onClick={() => updateActiveDetail(day, detail.filter((_, i) => i !== idx))}
                            >
                              ×
                            </Button>
                          </div>
                        )
                      })}
                      <Button
                       
                        size="small"
                        
                        className="h-7 w-full text-leaf-900 dark:border-gray-500"
                        onClick={() => {
                          const last = detail[detail.length - 1]
                          const nextEnd = last ? last.split("-")[1] || "23:00" : "08:00"
                          updateActiveDetail(day, [...detail, `${nextEnd}-23:00`])
                        }}
                      >
                        ＋ 添加时间段
                      </Button>
                      </>}
                    </div>
                  )
                })}
              </div>
</div></details></TabsContent>
        </Tabs>

        <div className="admin-user-edit__footer flex justify-end gap-2 pt-4">
          <Button  onClick={() => handleOpenChange(false)} disabled={loading}>
            <X className="mr-2 h-4 w-4" />
            取消
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            <Save className="mr-2 h-4 w-4" />
            {loading ? "保存中..." : "保存"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
